import { useState } from 'react';
import { type SecurityEvent } from '@/mocks/saSecurity';
import { EVENT_TYPE_LABELS, RISK_LEVEL_LABELS } from './securityLabels';

interface Props {
  events: SecurityEvent[];
  onSelect: (e: SecurityEvent) => void;
  onAction: (action: string, e: SecurityEvent) => void;
}

export default function SecurityEventsTable({ events, onSelect, onAction }: Props) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const safeEvents = Array.isArray(events) ? events : [];

  if (safeEvents.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-shield-check-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin eventos de seguridad</p>
        <p className="text-foreground-400 text-xs">No se encontraron eventos que coincidan con los filtros actuales.</p>
      </div>
    );
  }

  const toggleMenu = (id: string) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Evento</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Usuario</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">IP</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">País</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Dispositivo</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Riesgo</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Fecha</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-10"></th>
            </tr>
          </thead>
          <tbody>
            {safeEvents.map((e) => {
              const typeMeta = EVENT_TYPE_LABELS[e.type] || { label: e.type, icon: 'ri-question-line', color: 'bg-background-100 text-foreground-500' };
              const riskMeta = RISK_LEVEL_LABELS[e.riskLevel] || { label: e.riskLevel, color: 'bg-background-100 text-foreground-500' };

              return (
                <tr key={e.id} className="border-b border-background-200/60 hover:bg-background-100/50 transition-colors group">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${typeMeta.color}`}>
                        <i className={`${typeMeta.icon} text-[11px]`}></i>
                      </div>
                      <span className="text-foreground-800 text-xs font-medium">{typeMeta.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <p className="text-foreground-800 text-xs font-medium">{e.userName}</p>
                    <p className="text-foreground-400 text-[10px]">{e.userRole}</p>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-700 text-xs">{e.radioName}</span>
                  </td>
                  <td className="px-3 py-3">
                    <code className="text-foreground-600 text-[11px] bg-background-100 px-1.5 py-0.5 rounded">{e.ipAddress}</code>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-foreground-600 text-xs">{e.country}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="max-w-[160px]">
                      <p className="text-foreground-700 text-xs truncate">{e.device}</p>
                      <p className="text-foreground-400 text-[10px] truncate">{e.browser} · {e.os}</p>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${riskMeta.color}`}>
                      {riskMeta.label}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-500 text-[11px] whitespace-nowrap">{e.timestamp}</span>
                  </td>
                  <td className="px-3 py-3">
                    {e.resolved ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-medium">
                        <i className="ri-check-line text-[9px]"></i>
                        Resuelto
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-[10px] font-medium">
                        <i className="ri-time-line text-[9px]"></i>
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right relative">
                    <button
                      onClick={() => toggleMenu(e.id)}
                      className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-more-2-fill text-foreground-400 text-sm"></i>
                    </button>
                    {openMenuId === e.id && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)}></div>
                        <div className="absolute right-0 top-full mt-1 z-50 w-52 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1.5">
                          <button
                            onClick={() => { onSelect(e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-foreground-700 text-xs cursor-pointer hover:bg-background-100 transition-colors"
                          >
                            <i className="ri-eye-line text-sm"></i>
                            Ver detalle completo
                          </button>
                          <button
                            onClick={() => { onAction('investigate', e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-foreground-700 text-xs cursor-pointer hover:bg-background-100 transition-colors"
                          >
                            <i className="ri-search-eye-line text-sm"></i>
                            Investigar evento
                          </button>
                          <button
                            onClick={() => { onAction('link_audit', e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-foreground-700 text-xs cursor-pointer hover:bg-background-100 transition-colors"
                          >
                            <i className="ri-links-line text-sm"></i>
                            Ver en Audit Log
                          </button>
                          <div className="border-t border-background-200 my-1"></div>
                          <button
                            onClick={() => { onAction('block_ip', e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-red-600 text-xs cursor-pointer hover:bg-red-50 transition-colors"
                          >
                            <i className="ri-forbid-line text-sm"></i>
                            Bloquear IP
                          </button>
                          <button
                            onClick={() => { onAction('block_user', e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-red-600 text-xs cursor-pointer hover:bg-red-50 transition-colors"
                          >
                            <i className="ri-user-unfollow-line text-sm"></i>
                            Bloquear usuario
                          </button>
                          <button
                            onClick={() => { onAction('close_session', e); setOpenMenuId(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-red-600 text-xs cursor-pointer hover:bg-red-50 transition-colors"
                          >
                            <i className="ri-logout-box-r-line text-sm"></i>
                            Cerrar sesión
                          </button>
                          {!e.resolved && (
                            <button
                              onClick={() => { onAction('mark_safe', e); setOpenMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-emerald-600 text-xs cursor-pointer hover:bg-emerald-50 transition-colors"
                            >
                              <i className="ri-shield-check-line text-sm"></i>
                              Marcar como seguro
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}